'use client';

import * as SelectPrimitive from '@radix-ui/react-select';
import { SelectTriggerProps } from '@radix-ui/react-select';
import { cva, VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import * as React from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { StopPropagation } from 'v5/platform/react/StopPropagation';
import { focusRings } from './_common/focusRing';
import { usePortalContainer } from 'v5/primitives/PortalContainer';

const special_value_clear =
  '@@__clear__@@_4d9c8f32-8b9e-4f32-a0ae-512d943a7285';

/** What a `FormControl` gives its control; the root renders nothing, so the trigger takes it. */
type SelectControlProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
};

const SelectContext = React.createContext<{
  onClear?: () => void;
  clearable?: boolean;
  control?: SelectControlProps;
}>({});

const Select = (
  props: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root> &
    SelectControlProps & {
      onClear?: () => void;
      clearable?: boolean;
    }
) => {
  const {
    onValueChange,
    onClear,
    clearable,
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...restProps
  } = props;
  const control = {
    id,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid
  };

  return (
    <SelectContext.Provider value={{ onClear, clearable, control }}>
      <SelectPrimitive.Root
        {...restProps}
        onValueChange={value => {
          if (value === special_value_clear) {
            onClear?.();
          } else if (onValueChange) {
            onValueChange(value);
          }
        }}
      />
    </SelectContext.Provider>
  );
};
Select.displayName = SelectPrimitive.Root.displayName;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectItemText = SelectPrimitive.ItemText;

export const selectTriggerVariants = cva(
  [
    'group is-trigger',
    'flex w-full items-center justify-between  text-fg-default data-[placeholder]:text-fg-muted not-aria-readonly:disabled:cursor-not-allowed not-aria-readonly:disabled:opacity-50'
  ],
  {
    variants: {
      variant: {
        // when just functionality is needed, but classes come from other component's - ie. <InputGroupButton asChild><SelectTrigger variant="headless">...
        headless: '',
        regular: [
          focusRings.focus.visible,
          focusRings.focus.invalidFocused,
          focusRings.focus.invalidFocusedVisible,
          'rounded border border-border-default bg-bg-default',
          'not-aria-readonly:hocus:border-bg-inset aria-readonly:cursor-default'
        ],
        ghost: [
          focusRings.focus.visible,
          focusRings.focus.invalidFocused,
          focusRings.focus.invalidFocusedVisible,
          'rounded',
          'border border-transparent not-aria-disabled:not-aria-readonly:hocus:border-bg-inset not-aria-disabled:not-aria-readonly:hocus:bg-bg-subtle/30 text-fg-default aria-disabled:text-fg-default/50 not-aria-disabled:not-aria-readonly:hocus:border-charcoal-dark/10'
        ],
        inline: [
          'transition-all',
          'bg-bg-default',
          'bg-transparent text-fg-default',
          //
          'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_]',
          'aria-expanded:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-border-default',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus-within:shadow-brand-400 aria-expanded:shadow-brand-400'
        ],
        plain: [
          'transition-all',
          'bg-bg-default',
          'bg-transparent text-fg-default',
          //
          'not-aria-readonly:focus-within:shadow-[inset_0_-2px_0_0_]',
          'aria-expanded:shadow-[inset_0_-2px_0_0_]',
          'shadow-[inset_0_-1px_0_0_]',
          'shadow-transparent',
          'not-aria-readonly:hover:shadow-bg-inset',
          'not-aria-readonly:focus:shadow-brand-300',
          'not-aria-readonly:hover:focus-within:shadow-brand-400 aria-expanded:shadow-brand-400'
        ]
      },
      size: {
        xs: 'min-h-5 py-1 gap-2 text-base [&_.value-container>*]:flex [&_.value-container>*]:gap-2 [&_.value-container>*]:items-center [&_.value-container_svg]:size-4',
        sm: 'min-h-9 py-1.5 gap-2 text-base [&_.value-container>*]:flex [&_.value-container>*]:gap-2 [&_.value-container>*]:items-center [&_.value-container_svg]:size-4',
        md: 'min-h-12 py-2 gap-2 text-base [&_.value-container>*]:flex [&_.value-container>*]:gap-2 [&_.value-container>*]:items-center [&_.value-container_svg]:size-5',
        lg: 'min-h-15 py-3 gap-2 text-lg [&_.value-container>*]:flex [&_.value-container>*]:gap-2 [&_.value-container>*]:items-center [&_.value-container_svg]:size-6',
        xl: 'min-h-17 py-4 gap-2 text-xl [&_.value-container>*]:flex [&_.value-container>*]:gap-2 [&_.value-container>*]:items-center [&_.value-container_svg]:size-7'
      }
    },
    compoundVariants: [
      {
        variant: ['regular', 'ghost'],
        size: 'xs',
        className: 'px-3'
      },
      {
        variant: ['regular', 'ghost'],
        size: 'sm',
        className: 'px-3'
      },
      {
        variant: ['regular', 'ghost'],
        size: 'md',
        className: 'px-3'
      },
      {
        variant: ['regular', 'ghost'],
        size: 'lg',
        className: 'px-3'
      },
      {
        variant: ['regular', 'ghost'],
        size: 'xl',
        className: 'px-3'
      }
    ],
    defaultVariants: {
      size: 'md',
      variant: 'regular'
    }
  }
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps &
    VariantProps<typeof selectTriggerVariants> & { readOnly?: boolean }
>(
  (
    { className, size, variant, children, readOnly, asChild, ...props },
    ref
  ) => {
    const { onClear, clearable, control } = useContext(SelectContext);

    return (
      <SelectPrimitive.Trigger
        {...control}
        asChild={asChild}
        ref={ref}
        className={cn(
          !asChild && selectTriggerVariants({ size, variant }),
          readOnly && 'pointer-events-none cursor-default',
          className
        )}
        aria-readonly={readOnly ? 'true' : undefined}
        tabIndex={readOnly ? -1 : props.tabIndex}
        onKeyDown={e => {
          if (e.key === 'Backspace' && onClear && clearable) {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }
        }}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <SelectTriggerContent readOnly={readOnly}>
            {children}
          </SelectTriggerContent>
        )}
      </SelectPrimitive.Trigger>
    );
  }
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export const SelectTriggerContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  { children: React.ReactNode; readOnly?: boolean }
>(({ children, readOnly }, ref) => (
  <>
    <div className="value-container w-full flex flex-row items-center gap-1 *:contents truncate">
      {children}
    </div>
    {!readOnly && (
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-5 shrink-0  opacity-50" />
      </SelectPrimitive.Icon>
    )}
  </>
));

SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronUp className="size-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-1',
      className
    )}
    {...props}
  >
    <ChevronDown className="size-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal container={usePortalContainer()}>
    <StopPropagation>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-50 rounded-md border border-border-default bg-bg-default text-fg-default shadow-md',
          'origin-[var(--radix-select-content-transform-origin)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          'max-h-[calc(var(--radix-select-content-available-height)_-_var(--spacing))] overflow-y-auto overflow-x-hidden',
          'w-auto',
          'min-w-[max(var(--radix-select-trigger-width),120px)]',
          'max-w-[max(var(--radix-select-trigger-width),min(500px,var(--radix-select-content-available-width)))]',
          'p-1',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full'
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </StopPropagation>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'py-1.5 pl-2 pr-2 text-base font-semibold',
      '[&_svg,img]:pointer-events-none [&_svg,img]:size-5 [&_svg,img]:shrink-0',
      className
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const selectItemVariants = cva(
  [
    'transition-colors',
    'relative flex cursor-default select-none items-center rounded-sm pr-8  text-base outline-none focus:bg-bg-subtle focus:text-default data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
  ],
  {
    variants: {
      size: {
        md: 'text-base [&_[data-item-text]]:flex [&_[data-item-text]]:gap-2 [&_[data-item-text]]:p-2 [&_[data-item-text]]:leading-tight [&_[data-item-text]]:items-center [&_[data-item-text]_svg,img]:size-5',
        lg: 'text-base [&_[data-item-text]]:flex [&_[data-item-text]]:gap-4 [&_[data-item-text]]:p-4 [&_[data-item-text]]:leading-tight [&_[data-item-text]]:items-center [&_[data-item-text]_svg,img]:size-5'
      }
    },
    defaultVariants: {
      size: 'md'
    }
  }
);

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> &
    VariantProps<typeof selectItemVariants> & { renderCheckmark?: boolean }
>(({ className, children, size, renderCheckmark = true, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      selectItemVariants({ size }),
      renderCheckmark ? '' : 'pr-0',
      className
    )}
    {...props}
  >
    <SelectPrimitive.ItemText data-item-text="">
      {children}
    </SelectPrimitive.ItemText>
    {renderCheckmark && (
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
    )}
  </SelectPrimitive.Item>
));

const SelectItemClear = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, ...props }, ref) => {
  const { t } = useTranslation('ds');
  const { clearable } = useContext(SelectContext);
  return (
    <SelectItem
      disabled={!clearable}
      {...props}
      ref={ref}
      value={special_value_clear}
      className={cn('text-brand-500', className)}
    >
      <X />
      {t('Select.clear')}
    </SelectItem>
  );
});
const SelectEmpty = React.forwardRef<
  React.ElementRef<'div'>,
  React.ComponentPropsWithoutRef<'div'>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('py-1.5 pl-2 pr-2 text-base text-fg-muted', className)}
    {...props}
  >
    {children ?? 'No options available'}
  </div>
));

SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectItemText,
  SelectEmpty,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectItemClear
};
